import { IconButton } from '@platform/design-system';
import { ArrowDown, ArrowUp, GripVertical, Settings2, Trash2 } from 'lucide-react';
import type { DragEvent } from 'react';
import { usePageContext } from '../../usePageContext.ts';
import type { Shift } from './layout-editing.ts';
import styles from './DashboardGrid.module.css';

export type GridEditor = {
  readonly onShift: (id: string, shift: Shift) => void;
  readonly onDropAt: (id: string, index: number) => void;
  readonly onRemove: (id: string) => void;
  readonly onConfigure: (id: string) => void;
};

const DRAGGED_WIDGET = 'application/x-dashboard-widget';
const ICON_SIZE = 18;

export const dropProps = (editor: GridEditor, index: number) => ({
  onDragOver: (event: DragEvent<HTMLElement>) => {
    if (event.dataTransfer.types.includes(DRAGGED_WIDGET)) event.preventDefault();
  },
  onDrop: (event: DragEvent<HTMLElement>) => {
    const id = event.dataTransfer.getData(DRAGGED_WIDGET);
    if (id === '') return;
    event.preventDefault();
    editor.onDropAt(id, index);
  },
});

export type EditChromeProps = {
  editor: GridEditor;
  id: string;
  title: string;
  first: boolean;
  last: boolean;
};

export const EditChrome = ({ editor, id, title, first, last }: EditChromeProps) => {
  const { t } = usePageContext();
  return (
    <div className={styles['chrome']}>
      <span
        className={styles['handle']}
        draggable
        title={t('dashboard.widgets.drag', { title })}
        onDragStart={(event) => {
          event.dataTransfer.setData(DRAGGED_WIDGET, id);
          event.dataTransfer.effectAllowed = 'move';
        }}
      >
        <GripVertical size={ICON_SIZE} aria-hidden="true" />
        <span className={styles['chromeTitle']}>{title}</span>
      </span>
      <IconButton
        size="sm"
        label={t('dashboard.widgets.moveEarlier', { title })}
        icon={<ArrowUp size={ICON_SIZE} />}
        disabled={first}
        onClick={() => {
          editor.onShift(id, 'earlier');
        }}
      />
      <IconButton
        size="sm"
        label={t('dashboard.widgets.moveLater', { title })}
        icon={<ArrowDown size={ICON_SIZE} />}
        disabled={last}
        onClick={() => {
          editor.onShift(id, 'later');
        }}
      />
      <IconButton
        size="sm"
        label={t('dashboard.widgets.configure', { title })}
        icon={<Settings2 size={ICON_SIZE} />}
        onClick={() => {
          editor.onConfigure(id);
        }}
      />
      <IconButton
        size="sm"
        label={t('dashboard.widgets.remove', { title })}
        icon={<Trash2 size={ICON_SIZE} />}
        onClick={() => {
          editor.onRemove(id);
        }}
      />
    </div>
  );
};
